<template>
  <div class="velocity-viz-pro">
    <div class="viz-header-compact" >
      <div class="brief" v-latex="knowledgePoint"></div>
      <div class="formula-chip" v-latex>$v = Δx / Δt$</div>
    </div>

    <div class="main-layout vertical">
      <!-- 仿真区域 -->
      <div class="simulation-stage dark-stage">
        <svg width="100%" height="180" viewBox="0 0 400 180">
          <!-- 跑道 -->
          <line x1="20" y1="150" x2="380" y2="150" stroke="#334155" stroke-width="4" stroke-linecap="round" />
          <g v-for="i in 9" :key="i">
             <line :x1="20 + (i-1)*40" y1="150" :x2="20 + (i-1)*40" y2="155" stroke="#475569" stroke-width="1" />
             <text :x="20+(i-1)*40" y="168" fill="#64748b" font-size="8" text-anchor="middle">{{ (i-1)*5 }}m</text>
          </g>

          <!-- 轨迹 -->
          <polyline :points="tracePoints" fill="none" stroke="rgba(255,255,255,0.1)" stroke-width="1" />
          
          <!-- 物体 -->
          <g :transform="`translate(${posX}, 150)`">
             <rect x="-15" y="-12" width="30" height="12" fill="#3b82f6" rx="2" />
             <line x1="0" y1="-18" :x2="currentV * 5" y2="-18" stroke="#10b981" stroke-width="2" marker-end="url(#arr-v)" v-if="Math.abs(currentV) > 0" />
             <text v-if="Math.abs(currentV) > 0" :x="currentV * 5 + (currentV > 0?5:-5)" y="-15" fill="#10b981" font-size="10" :text-anchor="currentV > 0?'start':'end'">v</text>
          </g>

          <!-- 矢量指示器 -->
          <g transform="translate(20, 40)" v-if="mode === 'average'">
             <!-- 位移矢量 -->
             <line x1="0" y1="0" :x2="displacement * 8" y2="0" stroke="#f59e0b" stroke-width="3" marker-end="url(#arr-disp)" />
             <text :x="displacement * 8 + 5" y="5" fill="#f59e0b" font-size="11" font-weight="900">位移 Δx</text>
          </g>
        </svg>

        <div class="hud-panel-pro">
           <div class="hud-col">
              <div class="item">
                 <label>路程 (标量 s)</label>
                 <span>{{ totalDistance.toFixed(1) }} m</span>
              </div>
              <div class="item">
                 <label>位移 (矢量 Δx)</label>
                 <span>{{ displacement.toFixed(1) }} m</span>
              </div>
           </div>
           <div class="hud-col main">
              <div class="item big" :title="'s / t'">
                 <label>平均速率</label>
                 <span class="val">{{ avgSpeed.toFixed(2) }} <small>m/s</small></span>
              </div>
              <div class="item big" :title="'Δx / t'">
                 <label>平均速度</label>
                 <span class="val">{{ avgVelocity.toFixed(2) }} <small>m/s</small></span>
              </div>
           </div>
        </div>
      </div>

      <!-- 控制面板 -->
      <div class="compact-controls">
        <div class="control-card glass">
           <div class="mode-row">
              <el-radio-group v-model="mode" size="mini" @change="reset">
                 <el-radio-button label="instant">瞬时速度</el-radio-button>
                 <el-radio-button label="average">平均速度 vs 速率</el-radio-button>
              </el-radio-group>
           </div>
           
           <div class="action-row mt-sm">
              <el-button type="primary" size="small" @click="toggle" :icon="running ? 'el-icon-video-pause' : 'el-icon-video-play'">
                 {{ running ? '暂停' : '开始运动' }}
              </el-button>
              <el-button size="small" @click="reset">复位</el-button>
           </div>
        </div>

        <div class="discovery-card-compact" v-latex>
           <p v-if="mode === 'instant'"><strong>瞬时速度：</strong>物体在某一时刻或某一位置的速度，是矢量。</p>
           <p v-else><strong>核心区别：</strong>平均速度等于<strong>位移/时间</strong>（关注首尾）；平均速率等于<strong>路程/时间</strong>（关注全部路径）。</p>
        </div>
      </div>
    </div>

    <defs>
      <marker id="arr-v" markerWidth="6" markerHeight="4" refX="6" refY="2" orient="auto"><polygon points="0 0, 6 2, 0 4" fill="#10b981" /></marker>
      <marker id="arr-disp" markerWidth="6" markerHeight="4" refX="6" refY="2" orient="auto"><polygon points="0 0, 6 2, 0 4" fill="#f59e0b" /></marker>
    </defs>
  </div>
</template>

<script>
export default {
  name: 'VelocityViz',
  props: { knowledgePoint: { type: String, default: '' } },
  data() {
    return {
      mode: 'instant',
      posX: 20,
      t: 0,
      running: false,
      history: [],
      timer: null
    }
  },
  computed: {
    currentV() {
       if (!this.running) return 0;
       if (this.mode === 'instant') return 8;
       // Average mode: go right 30m then back 10m
       if (this.t < 40) return 6; // To 20 + 30*8 = 260? No, let's use physical units
       if (this.t < 60) return -4;
       return 0;
    },
    displacement() { return (this.posX - 20) / 8; },
    totalDistance() {
       if (this.mode === 'instant') return this.displacement;
       // In average mode: 
       const d1 = Math.min(this.t, 40) * 6 / 8;
       const d2 = Math.max(0, this.t - 40) * 4 / 8;
       return d1 + d2;
    },
    avgVelocity() { return this.t > 0 ? this.displacement / (this.t / 10) : 0; },
    avgSpeed() { return this.t > 0 ? this.totalDistance / (this.t / 10) : 0; },
    tracePoints() { return this.history.map(h => `${h.x},${150 - h.t}`).join(' '); }
  },
  watch: {
    knowledgePoint() { this.detectMode(); }
  },
  mounted() { this.detectMode(); },
  beforeDestroy() { if (this.timer) cancelAnimationFrame(this.timer); },
  methods: {
    detectMode() {
       const kp = (this.knowledgePoint || '').toString();
       if (kp.includes('平均') || kp.includes('速率')) this.mode = 'average';
       else this.mode = 'instant';
    },
    toggle() {
      this.running = !this.running;
      if (this.running) this.animate();
    },
    animate() {
       if (!this.running) return;
       this.t += 0.5;
       this.posX += this.currentV * 0.5;
       this.history.push({ x: this.posX, t: this.t });
       
       if (this.posX > 380 || (this.mode === 'average' && this.t > 60)) {
          this.running = false;
       } else {
          this.timer = requestAnimationFrame(this.animate);
       }
    },
    reset() {
       this.running = false;
       this.posX = 20; this.t = 0; this.history = [];
       if (this.timer) cancelAnimationFrame(this.timer);
    }
  }
}
</script>

<style scoped lang="scss">
.velocity-viz-pro { flex: 1; display: flex; flex-direction: column; gap: 8px; height: 100%; overflow: hidden; }
.viz-header-compact { display: flex; justify-content: space-between; align-items: center; padding: 0 4px; .brief { font-size: 13px; color: #1e293b; font-weight: 500; } .formula-chip { background: #f0fdf4; color: #10b981; border: 1px solid #bbf7d0; padding: 4px 14px; border-radius: 20px; font-weight: 700; font-family: monospace; } }
.simulation-stage { flex: 1; min-height: 180px; background: #020617; border-radius: 12px; border: 1.5px solid #1e293b; position: relative; overflow: hidden; .hud-panel { position: absolute; top: 12px; left: 12px; .hud-item { background: rgba(15, 23, 42, 0.82); border-radius: 8px; border: 1px solid #1e293b; padding: 4px 12px; label { display: block; font-size: 10px; color: #94a3b8; font-weight: 700; } .val { font-size: 13px; color: #22d3ee; font-weight: 800; } &.cyan { border-left: 3px solid #06b6d4; } } } }
.compact-controls { display: flex; flex-direction: column; gap: 8px; }
.control-card { background: #ffffff; padding: 12px 16px; border-radius: 12px; border: 1px solid #e2e8f0; }
.control-vertical-group { display: flex; flex-direction: column; gap: 10px; .param-row { .s-item { label { font-size: 11px; color: #64748b; font-weight: 700; display: block; margin-bottom: 2px; } } } }
.discovery-card-compact { background: #f8fafc; padding: 10px 14px; border-radius: 10px; border: 1px solid #e2e8f0; display: flex; align-items: center; gap: 10px; i { color: #6366f1; font-size: 1.1rem; } p { flex: 1; margin: 0; font-size: 12px; color: #475569; line-height: 1.5; strong { color: #1e293b; } } }
</style>
